import { useEffect, useRef, useState } from 'react';
import { Chip } from '@jotpsych/ui';
import HistoryIcon from '@mui/icons-material/History';
import LocalPharmacyOutlinedIcon from '@mui/icons-material/LocalPharmacyOutlined';
import { Box, Button, Skeleton, Stack, Typography } from '@mui/material';

import { formatIsoDate } from '../vm/formatters';

import {
  attributedFillsCopy,
  consentCtaLabel,
  consentNeededCopy,
  type ConsentState,
  emptyGrantedCopy,
  HISTORY_FILL_EXAMPLES,
  type HistoryFill,
  PULLING_COPY,
  unavailableCopy,
} from './medicationHistoryConsent';

interface MedicationHistorySectionProps {
  /** Org subject language ("patient"/"client"); hosts pass useSubjectLanguageVariants().singular. */
  subject?: string;
  /**
   * DEMO mode (design-review stories): starting consent state; the CTA
   * advances needed → pulling → granted locally. Ignored when `state` is set.
   */
  initialState?: ConsentState;
  /** CONTROLLED mode (containers): the server-reported consent state. */
  state?: ConsentState;
  /** Fills shown once consent is granted. */
  fills?: HistoryFill[];
  /**
   * SureScripts fills that matched charted medications and render in those
   * medications' drawers instead of here. When `fills` is empty but this is
   * positive, the empty state explains where the fills went.
   */
  attributedFillCount?: number;
  /**
   * CONTROLLED mode: records consent via the host container (the real
   * mutation). When provided, the local demo simulation is disabled.
   */
  onRecordConsent?: () => void;
}

/** Milliseconds the "pulling" state shows before flipping to granted (demo). */
const PULL_SIMULATION_MS = 1200;

const SectionHeader: React.FC = () => (
  <Stack direction="row" spacing={1} sx={{ alignItems: 'center', mb: 0.5 }}>
    <HistoryIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
    <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
      Medication history
    </Typography>
    <Typography variant="caption" color="text.disabled">
      from pharmacies · SureScripts
    </Typography>
  </Stack>
);

const NeedsConsent: React.FC<{ onConsent: () => void; subject: string }> = ({
  onConsent,
  subject,
}) => (
  <Box sx={{ py: 2, textAlign: 'center' }}>
    <Typography
      variant="body2"
      color="text.secondary"
      sx={{ mb: 1.5, maxWidth: 480, mx: 'auto' }}
    >
      {consentNeededCopy(subject)}
    </Typography>
    <Button variant="contained" size="small" onClick={onConsent} sx={{ textTransform: 'none' }}>
      {consentCtaLabel(subject)}
    </Button>
  </Box>
);

const Pulling: React.FC = () => (
  <Box sx={{ py: 1 }}>
    {[0, 1, 2, 3].map((i) => (
      <Skeleton key={i} variant="rounded" height={28} sx={{ my: 0.75, borderRadius: 1 }} />
    ))}
    <Typography
      variant="caption"
      color="text.secondary"
      sx={{ display: 'block', textAlign: 'center', mt: 1 }}
    >
      {PULLING_COPY}
    </Typography>
  </Box>
);

const FillRow: React.FC<{ fill: HistoryFill }> = ({ fill }) => {
  const dispensed = [fill.quantity, fill.daysSupply ? `${fill.daysSupply}-day` : null]
    .filter(Boolean)
    .join(' · ');
  return (
    <Stack
      direction="row"
      spacing={1.5}
      sx={{
        alignItems: 'flex-start',
        py: 1,
        borderBottom: '1px solid',
        borderColor: 'divider',
        '&:last-child': { borderBottom: 'none' },
      }}
    >
      <LocalPharmacyOutlinedIcon sx={{ fontSize: 18, color: 'text.disabled', mt: 0.25 }} />
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Stack direction="row" spacing={0.75} useFlexGap sx={{ flexWrap: 'wrap', alignItems: 'center' }}>
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            {fill.drugName}
          </Typography>
          {fill.isControlled && (
            <Chip variant="soft" size="xSmall" color="warning" label="Controlled" />
          )}
        </Stack>
        <Typography
          variant="caption"
          color={fill.sig ? 'text.secondary' : 'text.disabled'}
          sx={{ display: 'block', fontStyle: fill.sig ? 'normal' : 'italic' }}
        >
          {fill.sig ?? 'Directions not on file'}
        </Typography>
        {fill.indication && (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
            For {fill.indication}
          </Typography>
        )}
      </Box>
      <Box sx={{ textAlign: 'right', flexShrink: 0 }}>
        <Typography variant="caption" sx={{ display: 'block', fontWeight: 600 }}>
          {fill.payer}
        </Typography>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
          Filled {formatIsoDate(fill.lastFillDate)}
        </Typography>
        {dispensed && (
          <Typography variant="caption" color="text.disabled" sx={{ display: 'block' }}>
            {dispensed}
          </Typography>
        )}
      </Box>
    </Stack>
  );
};

const Granted: React.FC<{
  fills: HistoryFill[];
  subject: string;
  attributedFillCount: number;
}> = ({ fills, subject, attributedFillCount }) => {
  if (fills.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary" sx={{ py: 1.5 }}>
        {attributedFillCount > 0
          ? attributedFillsCopy(subject, attributedFillCount)
          : emptyGrantedCopy(subject)}
      </Typography>
    );
  }
  return (
    <Box>
      {fills.map((fill) => (
        <FillRow key={fill.id} fill={fill} />
      ))}
    </Box>
  );
};

/**
 * Consent-gated pharmacy medication-history section for Proposal 0. Lives in the
 * same card as the current regimen (Edwin's "one place"), and renders one of the
 * four real consent states the unified endpoint reports. The CTA advances the
 * state locally so the gated → pulling → granted flow can be walked in Storybook.
 */
export const MedicationHistorySection: React.FC<MedicationHistorySectionProps> = ({
  subject = 'patient',
  initialState = 'granted',
  state: controlledState,
  fills = HISTORY_FILL_EXAMPLES,
  attributedFillCount = 0,
  onRecordConsent,
}) => {
  const [demoState, setDemoState] = useState<ConsentState>(initialState);
  const pullTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => setDemoState(initialState), [initialState]);
  useEffect(
    () => () => {
      if (pullTimer.current) clearTimeout(pullTimer.current);
    },
    [],
  );

  // Demo only: recording consent shows the brief "pulling" state, then the
  // fills — the real needed → pulling → granted flow, walkable from stories.
  const advanceDemo = () => {
    setDemoState('pulling');
    pullTimer.current = setTimeout(() => setDemoState('granted'), PULL_SIMULATION_MS);
  };

  const state = controlledState ?? demoState;
  const handleConsent = onRecordConsent ?? advanceDemo;

  // Hidden entirely when the patient isn't e-prescribing-connected AND the
  // host is driving the state (matches the shipped V2 section's behavior);
  // demo mode still renders the explanatory copy for design review.
  if (controlledState === 'unavailable') return null;

  return (
    <Box
      sx={{
        px: 2,
        py: 2,
        borderTop: '1px solid',
        borderColor: 'divider',
        bgcolor: 'backgroundColors.secondary',
      }}
    >
      <SectionHeader />
      {state === 'unavailable' && (
        <Typography variant="body2" color="text.secondary" sx={{ py: 1 }}>
          {unavailableCopy(subject)}
        </Typography>
      )}
      {state === 'needed' && <NeedsConsent onConsent={handleConsent} subject={subject} />}
      {state === 'pulling' && <Pulling />}
      {state === 'granted' && (
        <Granted fills={fills} subject={subject} attributedFillCount={attributedFillCount} />
      )}
    </Box>
  );
};
